import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Avatar,
  Alert
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useDispatch, useSelector } from 'react-redux';
import { saveFormDetails } from "../redux/slices/CreateAgentSlice";
import { FORM_KEYS } from '../utills/constant';

function AgentName({ open, onClose, onclick }) {
  const dispatch = useDispatch();
  const { deployAgentLoading } = useSelector((state) => state.DeploySlice);
  const { formDetails } = useSelector((state) => state.CreateAgentSlice);
  const { agentList } = useSelector((state) => state.LoadAIAgentsSlice);

  const [nameError, setNameError] = useState('');

  const setFormDetailsValue = (key, value) => {
    dispatch(saveFormDetails({ key, value }));
  };

  const handleCreateAgent = () => {
    // Empty Validation
    const nameValue = formDetails[FORM_KEYS.NAME]?.trim();
    if (!nameValue) {
      setNameError('Agent name should not be empty');
      return;
    }

    // Regex Validation
    const validNameRegex = /^(?!.*[ _-]{2})(?![_\-\s])[a-zA-Z _-]{3,20}(?<![_\-\s])$/;
    if (!validNameRegex.test(nameValue)) {
      setNameError('Please enter a valid Agent name');
      return;
    }

    // Duplicate Validation
    const isDuplicate = agentList?.some(
      (agent) => agent.name.toLowerCase() === nameValue.toLowerCase()
    );

    if (isDuplicate) {
      setNameError('Agent name already exists');
      return;
    }
    onclick();
  };

  const handleClose = () => {
    setFormDetailsValue(FORM_KEYS.NAME, '');
    setNameError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '500px',
          margin: 'auto',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: '#3B5CFF',
              color: 'white'
            }}
          >
            <PersonAddIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>
              Create New Agent
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              Give your AI agent a unique name
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleClose}
          sx={{
            minWidth: 'auto',
            p: 1,
            color: '#6c757d',
            '&:hover': {
              backgroundColor: '#e9ecef'
            }
          }}
        >
          <CancelOutlinedIcon fontSize="small" />
        </Button>
      </Box>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#495057',
              mb: 2,
              lineHeight: 1.6
            }}
          >
            Choose a descriptive name for your AI agent. This name will be used to identify your agent across the platform.
          </Typography>
          <TextField
            value={formDetails[FORM_KEYS.NAME]}
            placeholder="e.g., Invoice Processor, Document Analyzer, Data Extractor"
            fullWidth
            variant="outlined"
            onChange={(event) => setFormDetailsValue(FORM_KEYS.NAME, event.target.value)}
            error={nameError !== ''}
            // helperText={nameError}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#fff',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3B5CFF',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3B5CFF',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#6c757d',
              },
              '& .MuiInputBase-input': {
                fontSize: '16px',
                padding: '16px 20px',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '14px',
                marginLeft: '4px',
                marginTop: '8px',
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          {nameError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {nameError}
            </Alert>
          )}
          <Alert severity="info">
            * Should be unique <br />
            * Should contain min 3 and max 20 characters <br />
            * Only letters allowed <br/>
            * Allowed special characters - Underscore and Hyphen
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 3,
        gap: 2,
        justifyContent: 'flex-end'
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={deployAgentLoading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderColor: '#6c757d',
            color: '#6c757d',
            '&:hover': {
              borderColor: '#495057',
              backgroundColor: '#f8f9fa'
            },
            '&:disabled': {
              borderColor: '#dee2e6',
              color: '#adb5bd'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateAgent}
          variant="contained"
          disabled={deployAgentLoading}
          startIcon={deployAgentLoading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5,
            backgroundColor: '#3B5CFF',
            boxShadow: '0 4px 15px rgba(59, 92, 255, 0.3)',
            '&:hover': {
              backgroundColor: '#2E4BC0',
              boxShadow: '0 6px 20px rgba(59, 92, 255, 0.4)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: '#6c757d',
              boxShadow: 'none',
              transform: 'none'
            },
            transition: 'all 0.3s ease',
          }}
        >
          {deployAgentLoading ? 'Creating Agent...' : 'Create Agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AgentName;
